import axios from 'axios';
import authService from './authService';
import { API_BASE_URL } from '../config/api';

const payrollApi = axios.create({
  baseURL: `${API_BASE_URL}/payrolls`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const payrollCalculatorApi = axios.create({
  baseURL: `${API_BASE_URL}/v1/payroll`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token
const attachAuth = (config) => {
  const token = authService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

payrollApi.interceptors.request.use(
  (config) => {
    return attachAuth(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

payrollCalculatorApi.interceptors.request.use(
  (config) => {
    return attachAuth(config);
  },
  (error) => Promise.reject(error)
);

export const payrollService = {
  getAll: () => payrollApi.get(''),
  getByEmployee: (employeeId) => payrollApi.get(`/employee/${employeeId}`),
  getById: (id) => payrollApi.get(`/${id}`),
  create: (payroll) => payrollApi.post('', payroll),
  update: (id, payroll) => payrollApi.put(`/${id}`, payroll),
  getDefaultBonuses: (employeeId, year, month, preview = null) => {
    const params = { employeeId, year, month };
    if (preview && preview.includeOvertime) {
      if (preview.baseSalary != null && preview.baseSalary !== '') {
        params.baseSalary = preview.baseSalary;
      }
      if (preview.workedDays != null && preview.workedDays !== '') {
        params.workedDays = preview.workedDays;
      }
      if (preview.overtimeHours != null && preview.overtimeHours !== '') {
        params.overtimeHours = preview.overtimeHours;
      }
    }
    return payrollApi.get('/default-bonuses', { params });
  },
  calculate: (id) => payrollApi.put(`/${id}/calculate`),
  validate: (id) => payrollApi.put(`/${id}/validate`),
  markAsPaid: (id, sendEmail = true) => payrollApi.put(`/${id}/mark-paid`, null, {
    params: { sendEmail }
  }),
  batchCalculate: (year, month) => payrollApi.put('/batch/calculate', null, { params: { year, month } }),
  batchValidate: (year, month) => payrollApi.put('/batch/validate', null, { params: { year, month } }),
  batchMarkPaid: (year, month, sendEmail = false) =>
    payrollApi.put('/batch/mark-paid', null, { params: { year, month, sendEmail } }),
  delete: (id) => payrollApi.delete(`/${id}`),
  calculateNetSalary: (payload) => payrollCalculatorApi.post('/calculate', payload),
  getMyPayrolls: () => payrollApi.get('/my'),
  downloadPdf: async (id) => {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/payrolls/${id}/download-pdf`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulletin_paie_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  sendEmail: (id) => payrollApi.post(`/${id}/send-email`),
  getMonthlyHours: (employeeId, year, month) =>
    payrollApi.get('/monthly-hours', {
      params: { employeeId, year, month },
    }),
  getAnnualLeaveDays: (employeeId, year, month) =>
    payrollApi.get('/annual-leave-days', {
      params: { employeeId, year, month },
    }),
  downloadPeriodPdfs: (year, month, mode = 'zip') =>
    payrollApi.get('/batch/print', {
      params: { year, month, mode },
      responseType: 'blob',
    }),
  generateMonthlyStatement: (year, month, format = 'pdf') => {
    return payrollApi.get('/statement/monthly', {
      params: { year, month, format },
      responseType: 'blob', // Important pour les fichiers binaires
    });
  },
  generateQuarterlyCnssReport: (year, quarter) => {
    return payrollApi.get('/cnss-report/quarterly', {
      params: { year, quarter },
      responseType: 'blob', // Important pour les fichiers binaires
    });
  },
  generateAnnualCnssReport: (year) => {
    return payrollApi.get('/cnss-report/annual', {
      params: { year },
      responseType: 'blob', // Important pour les fichiers binaires
    });
  },
  generateQuarterlyDeclarationFile: (year, quarter) => {
    return payrollApi.get('/cnss-declaration/quarterly', {
      params: { year, quarter },
      responseType: 'blob', // Important pour les fichiers binaires
    });
  },
  downloadMyPdf: async (id) => {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/payrolls/my/${id}/download-pdf`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mon_bulletin_paie_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export default payrollService;
